import { Component, Input, OnInit } from '@angular/core'
import { UiService } from 'src/app/services/ui.service'
import { Application } from '../../../services/application.service'
import { Subscription } from 'rxjs'
import { ApiService } from '../../../../services/api.service'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { finalize, switchMap } from 'rxjs/operators'
import { handleArpioError } from '../../../../../utils/handle-error'
import { S3RecoveryBucketNameConflictIssue } from '../../../services/issue'
import { S3BucketConfig } from '../../../services/sync-pair.service'
import { ViewableIssue } from '../../issues-view/issues-view.component'
import { ResourceConfigDto } from '../../../../services/dtos'
import { AccountService } from '../../../services/account.service'

@Component({
  selector: 'bucket-name-conflict-issue',
  templateUrl: './bucket-name-conflict-issue.component.html',
  styleUrls: ['./bucket-name-conflict-issue.component.scss'],
  standalone: false,
})
export class BucketNameConflictIssueComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  @Input('application') application: Application = null

  private loadingSubscription: Subscription = null
  private loadingAccountConfigsSubscription: Subscription = null
  private savingSubscription: Subscription = null
  private saved = false
  private config: S3BucketConfig = null
  public accountRecoveryBucketSuffix: string = null
  constructor(
    public uiService: UiService,
    public apiService: ApiService,
    public accountService: AccountService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    let getConfig$ = this.application.getSyncPairResourceConfig$(this.issue.arn)
    this.loadingSubscription = getConfig$.pipe(finalize(() => (this.loadingSubscription = null))).subscribe(
      (resourceConfig) => {
        if (!!resourceConfig) {
          this.config = resourceConfig.resourceConfig as S3BucketConfig
          if (this.config) {
            this.configFormModel.setValue({
              recoveryBucketSuffix: this.config.recoveryBucketSuffix,
            })
          }
        }
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )

    this.loadingAccountConfigsSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((_accountId) => this.accountService.resourceTypeConfigs$),
        finalize(() => (this.loadingAccountConfigsSubscription = null))
      )
      .subscribe(
        (dto) => {
          this.accountRecoveryBucketSuffix = dto.configs['s3Bucket']?.recoveryBucketSuffix || null
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  configFormModel = this.formBuilder.group({
    recoveryBucketSuffix: [
      null,
      [Validators.minLength(2), Validators.maxLength(12), Validators.pattern('^[a-z0-9]+$')],
    ],
  })

  saveClick() {
    let config = {
      resourceConfigType: 's3Bucket',
      recoveryBucketSuffix: this.recoveryBucketSuffix,
      backfillDays: this.config.backfillDays,
      customerManagesReplication: this.config.customerManagesReplication,
      objectReplicationMode: this.config.objectReplicationMode,
    } as ResourceConfigDto

    let saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
    this.savingSubscription = saver$
      .pipe(
        //switchMap((_config) => this.issue.deleter$()),
        finalize(() => (this.savingSubscription = null))
      )
      .subscribe(
        (_response) => {
          this.saved = true
          //this.disableControls();
        },
        (err) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        }
      )
  }

  get primaryBucketName() {
    return (this.issue.issue as S3RecoveryBucketNameConflictIssue).primaryBucketName
  }

  get recoveryBucketName() {
    return (this.issue.issue as S3RecoveryBucketNameConflictIssue).recoveryBucketName
  }

  get recoveryBucketSuffix() {
    return this.configFormModel.value.recoveryBucketSuffix
  }

  get newRecoveryBucketName() {
    const suffix = this.recoveryBucketSuffix || this.defaultSuffix
    const maxRootLength = 62 - suffix.length
    const rootName = this.primaryBucketName.substring(0, maxRootLength)
    return `${rootName}-${suffix}`.toLowerCase()
  }

  get saving() {
    return !!this.savingSubscription && !this.savingSubscription.closed
  }

  get saveStatus() {
    if (this.saving) {
      return 'saving'
    }
    if (this.saved) {
      return 'saved'
    }
    return 'unsaved'
  }

  get validConfig() {
    return !this.configFormModel.invalid
  }

  get accountResourceConfigUrl() {
    return this.uiService.buildResourceConfigsUrl(this.application.accountId)
  }

  get defaultSuffix() {
    return this.accountRecoveryBucketSuffix || 'dr'
  }
}
